// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { URL } from '../../service/Service';
import useFetchIDPartner from '../useFetchIDPartner';
import AccountBalance from '../AccountBalance';
import Breadcrumb from '../../components/breadcrumb/breadcrumb';
import { useTranslation } from '../../i18n/LanguageContext';
import serviceImages from '../../scenes/rates/serviceImages';
import { CreditCardIcon } from '@heroicons/react/24/outline';

const getServicePresentation = (service) => {
  const normalized = String(service || '').trim().toUpperCase();

  if (['MBW', 'MW', 'MBWAY'].includes(normalized)) {
    return { label: 'MB WAY', image: serviceImages.MBWay };
  }

  if (['MB', 'MBMM', 'MBLP', 'MBDL', 'MBSRT', 'MBSD', 'MBTR', 'MBEG', 'MBRF', 'MBLI', 'PC', 'MULTIBANCO'].includes(normalized)) {
    return { label: 'Multibanco', image: serviceImages.Multibanco };
  }

  if (['CC', 'CC3DS', 'CREDITCARD'].includes(normalized)) {
    return { labelKey: 'rates.creditCard', image: serviceImages.CreditCard };
  }

  if (['GP', 'GOOGLEPAY', 'GOOGLE PAY'].includes(normalized)) {
    return { label: 'Google Pay', image: serviceImages.GooglePay };
  }

  if (['PA', 'APPLEPAY', 'APPLE PAY'].includes(normalized)) {
    return { label: 'Apple Pay', image: serviceImages.ApplePay };
  }

  if (['PX', 'PIX', 'EUROPIX'].includes(normalized)) {
    return { label: 'EuroPix', image: serviceImages.EuroPix };
  }

  if (['CP', 'COFIDIS'].includes(normalized)) {
    return { label: 'Cofidis', image: serviceImages.Cofidis };
  }

  if (['PF', 'PAYSAFECARD'].includes(normalized)) {
    return { label: 'Paysafecard', image: serviceImages.Paysafecard };
  }

  if (['PSC', 'PAYSAFECASH'].includes(normalized)) {
    return { label: 'Paysafecash', image: serviceImages.Paysafecash };
  }

  if (['SC', 'SANTANDER'].includes(normalized)) {
    return { label: 'Santander', image: serviceImages.Santander };
  }

  if (['DD', 'DB', 'DIRECTDEBIT'].includes(normalized)) {
    return { labelKey: 'rates.directDebit' };
  }

  if (normalized === 'CREDORAXTPA') {
    return { label: 'Credorax TPA' };
  }

  return { label: service || '—' };
};

export default function ActiveRatesUser() {
  const { t, language } = useTranslation();
  const [rates, setRates] = useState([]);
  const [error, setError] = useState(null);
  const jwtToken = sessionStorage.getItem('jwt');
  const pages = [{ name: t('rates.breadcrumb'), href: '/user/taxas', current: true }];
  const { loading, partnerID } = useFetchIDPartner(jwtToken);

  useEffect(() => {
    if (!loading && partnerID) {
      const getClients = async () => {
        try {
          const res = await fetch(`${URL}/api/partners/${partnerID}/rates`, {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              'Content-Type': 'application/json',
            },
          });
          if (res.ok) {
            const data = await res.json();
            setRates(data);
          } else {
            throw new Error('Failed to fetch data');
          }
        } catch (error) {
          setError(error);
        }
      };

      getClients();
    }
  }, [loading, partnerID, jwtToken]);

  return (
    <div className="mx-4 sm:mx-10">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center justify-between items-center">
        <Breadcrumb pages={pages} />
        <AccountBalance />
      </div>

      <div className="py-6">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          {t('rates.title')}
        </h1>
        <p className="mt-2 text-sm text-gray-500">{t('rates.description')}</p>
      </div>

      {error && (
        <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {t('rates.loadError')}
        </p>
      )}

      {rates.length === 0 ? (
        <div className="text-center text-gray-500">
          <p>{t('rates.none')}</p>
          {/* You can add more information or call to action here */}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rates.map((rate) => {
            const service = getServicePresentation(rate.nameService);
            const serviceLabel = service.labelKey ? t(service.labelKey) : service.label;

            return (
            <article key={rate.id} className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-green-cl/20 hover:shadow-lg">
              <div className="px-6 py-6">
                <div className="mb-5 flex items-center gap-4">
                  <div className="flex h-14 w-16 shrink-0 items-center justify-center rounded-2xl border border-gray-100 bg-gray-50 p-2.5 transition group-hover:bg-emerald-50">
                    {service.image ? (
                      <img
                        src={service.image}
                        alt={serviceLabel}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <CreditCardIcon className="h-7 w-7 text-green-cl" aria-hidden="true" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-gray-400">
                      {t('rates.paymentMethod')}
                    </p>
                    <h3 className="mt-1 truncate text-lg font-extrabold text-gray-900">
                      {serviceLabel}
                    </h3>
                  </div>
                </div>

                <div className="mb-2 text-sm font-bold text-gray-700">
                  {rate.rateType === 'FIXED_RATE' && t('rates.fixed')}
                  {rate.rateType === 'VARIABLE_RATE' && t('rates.variable')}
                  {rate.rateType === 'MIXED_RATE' && t('rates.mixed')}
                </div>
                <div className="flex items-center text-2xl font-bold text-green-900">
                  {rate.rateType === 'FIXED_RATE' && (
                    <>
                      {rate.rateValue.toLocaleString(language === 'pt' ? 'pt-PT' : language, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}€
                    </>
                  )}

                  {rate.rateType === 'VARIABLE_RATE' && (
                    <>
                      {rate.ratePercentage}%
                    </>
                  )}

                  {rate.rateType === 'MIXED_RATE' && (
                    <>
                      {rate.rateValue.toLocaleString(language === 'pt' ? 'pt-PT' : language, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                      <span className="mr-1">€</span>
                      <span className="mx-1">+</span>
                      {rate.ratePercentage}%
                    </>
                  )}
                </div>
              </div>
            </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
